import {Component, Host, h, State, EventEmitter, Event, Prop} from '@stencil/core';
import moment, {Moment} from "moment";
import {
  NestDatePickerChangeView,
  NestDatePickerDay,
  NestDatePickerView
} from '../nest-date-picker.interfaces';
import {NestDatePickerHeader} from "../components/nest-date-picker-header";

@Component({
  tag: 'nest-date-picker-day',
  styleUrl: '../nest-date-picker.scss',
  shadow: true,
})
export class NestDatePickerDayView {
  dayNames: string[] = moment.weekdaysShort();
  currentDate: Moment = moment();

  @State() rows = [];
  @State() headerLabel: { month: string; year: string };
  @State() headerDisabled = {};

  @Prop({mutable: true}) activeDate: Moment;
  @Prop({mutable: true}) selectedDate: Moment;
  @Prop() minDate: Moment;
  @Prop() maxDate: Moment;

  @Event() selectAnswer: EventEmitter<Moment>;
  @Event() changeView: EventEmitter<NestDatePickerChangeView>;

  private generateView() {
    const selectedDate: Moment =  this.activeDate || this.selectedDate;

    let date: Moment = selectedDate.clone().startOf("month").startOf("week");
    let endDate: Moment = selectedDate.clone().endOf("month").endOf("week");

    let monthStart = moment().year(selectedDate.year()).month(selectedDate.month()).startOf('month');
    let monthEnd = moment().year(selectedDate.year()).month(selectedDate.month()).endOf('month');

    this.headerDisabled = {
      prev: this.minDate && !selectedDate.isAfter(this.minDate, "month"),
      next: this.maxDate && !selectedDate.isBefore(this.maxDate, "month")
    }

    this.rows = [];
    while(date.isBefore(endDate)) {
      const week = [];
      for (let day = 0; day < 7; day++) {
        week.push({
          id: date.date(),
          label: date.format("dd"),
          value: date.format("DD-MM-YYYY"),
          year: date.year(),
          month: date.month(),
          date: date.clone(),
          current: date.isSame(this.currentDate, "day"),
          selected: this.selectedDate && date.isSame(this.selectedDate, 'day'),
          disabled: this.minDate && date.isBefore(this.minDate, "day")
            || this.maxDate && date.isAfter(this.maxDate, "day"),
          hide: date.isAfter(monthEnd) || date.isBefore(monthStart)
        })
        date.add(1, 'day')
      }
      this.rows.push(week);
    }

    this.headerLabel = {
      month: selectedDate.clone().format('MMMM'),
      year: selectedDate.clone().format('YYYY')
    }
  }

  private choose(day: NestDatePickerDay): void {
    if (day.disabled) return;
    this.activeDate = day.date.clone();

    if(!this.activeDate.isSame(this.selectedDate)) {
      this.selectedDate = this.activeDate;
    }

    this.changeView.emit({ answer: this.activeDate })
    this.selectAnswer.emit(this.selectedDate)
  }

  componentWillLoad() {
    this.generateView()
    this.setView('day')
  }

  private next(): void {
    this.activeDate.add(1, 'month')
    this.generateView()
  }

  private prev(): void {
    this.activeDate.subtract(1, 'month')
    this.generateView()
  }

  private setView(view: NestDatePickerView) {
    this.changeView.emit({view: view, answer: this.activeDate})
  }

  render() {
    const {month, year} = this.headerLabel
    return (
      <Host>
        <NestDatePickerHeader
          actions={[
            {label: month, handler: () => this.setView('month')},
            {label: year, handler: () => this.setView('year')},
          ]}
          nextHandler={this.next.bind(this)}
          prevHandler={this.prev.bind(this)}
          disabled={this.headerDisabled}
        />

        {this.dayNames && (
          <div class="flex pb-1.5">
            {this.dayNames.map(day =>
              <span class="m-px w-10 block text-center text-sm text-gray-500">{day}</span>
            )}
          </div>
        )}

        {this.rows.map(row => (
          <div class='flex'>
            {row.map((item: NestDatePickerDay) => (
              <div class={{
                'nest-date-picker-day': true,
                'bg-gray-100 first:rounded-s-full last:rounded-e-full': item.isRange,
                'bg-gray-100 rounded-s-full': item.startRange || item.endRange,
                'bg-gray-100 rounded-full': item.current,
                'nest-date-picker-day--selected': item.selected,
                'text-orange': item.hide
              }}>
                <button
                  onClick={() => this.choose(item)}
                  disabled={item.disabled}
                  type="button"
                >
                  {item.id}
                </button>
              </div>
            ))}
          </div>
        ))}
      </Host>
    )
  }
}
